/**
 * DashboardSkeleton — the grey-shimmer shape of the Dashboard shown while a
 * month loads (Splash & Loading rule 2: a skeleton, not a spinner). It mirrors
 * the real layout — hero leftover figure, three bank cards, four expense rows —
 * so the wait shows what is coming, not a blank. This is what in-app navigation
 * shows after the first load; the full splash only appears on a cold load.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
const Bar = ({ className = '' }) => <div className={`v-skel ${className}`} />;

export default function DashboardSkeleton() {
  return (
    <div className="min-h-full w-full px-4 md:px-8 pb-28" aria-busy="true">
      <div className="flex items-center justify-between py-4">
        <div className="flex items-center gap-2">
          <img src="/logo.svg" alt="" className="w-8 h-8 v-breathe" />
          <Bar className="h-6 w-40 rounded-md" />
        </div>
        <Bar className="h-6 w-28 rounded-md" />
      </div>

      {/* hero — the leftover figure */}
      <div className="mt-2">
        <Bar className="h-3 w-40 rounded" />
        <Bar className="h-12 w-64 max-w-[70%] rounded-lg mt-3" />
        <Bar className="h-3 w-56 max-w-[80%] rounded mt-3" />
      </div>

      {/* three bank cards */}
      <div className="grid gap-3 mt-6" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))' }}>
        {[0, 1, 2].map((i) => (
          <div key={i} className="bg-panel border border-edge rounded-2xl p-5 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Bar className="h-8 w-8 rounded-lg" />
              <Bar className="h-4 w-24 rounded" />
            </div>
            <Bar className="h-6 w-28 rounded" />
            <Bar className="h-3 w-20 rounded" />
          </div>
        ))}
      </div>

      {/* section label + four expense rows */}
      <Bar className="h-4 w-28 rounded mt-8" />
      <div className="flex flex-col gap-2 mt-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-panel border border-edge rounded-2xl p-4 flex items-center gap-3">
            <Bar className="h-6 w-6 rounded-full" />
            <div className="flex-1">
              <Bar className="h-4 w-1/2 rounded" />
              <Bar className="h-3 w-1/3 rounded mt-2" />
            </div>
            <Bar className="h-5 w-16 rounded" />
          </div>
        ))}
      </div>

      <div className="text-center text-xs text-slate-500 mt-6">Loading your month…</div>
    </div>
  );
}
